// src/app/confirm-account.jsx
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

export default function ConfirmAccount() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenStatus, setTokenStatus] = useState('checking'); // checking, valid, invalid, expired
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenStatus('invalid');
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/validate-confirmation-token?token=${token}`);
        
        if (response.data.valid) {
          setTokenStatus('valid');
          setUserInfo(response.data.user);
        } else {
          setTokenStatus(response.data.expired ? 'expired' : 'invalid');
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setTokenStatus('invalid');
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async () => {
    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;
    if (!specialCharRegex.test(password)) {
      alert('Password must contain at least one special character');
      return;
    }

    try {
      setLoading(true);
      
      const response = await axios.post(`${API_BASE_URL}/api/confirm-account-with-password`, {
        token,
        password
      });

      if (response.data.success) {
        alert('Account confirmed successfully! You can now log in.');
        // Force a page refresh when navigating to login to clear any cached state
        window.location.href = '/login';
      } else {
        alert('Failed to confirm account. Please try again.');
      }
    } catch (error) {
      console.error('Confirmation error:', error);
      alert('Failed to confirm account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (tokenStatus === 'checking') {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Validating account...</Text>
      </View>
    );
  }

  if (tokenStatus === 'invalid') {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Invalid Link</Text>
        <Text style={styles.message}>
          This confirmation link is invalid or has been used already.
        </Text>
        <TouchableOpacity 
          style={styles.button} 
          onPress={() => navigate('/login')}
        >
          <Text style={styles.buttonText}>Go to Login</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (tokenStatus === 'expired') {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Link Expired</Text>
        <Text style={styles.message}>
          This confirmation link has expired. Please contact your administrator to resend the invitation.
        </Text>
        <TouchableOpacity 
          style={styles.button} 
          onPress={() => navigate('/login')}
        >
          <Text style={styles.buttonText}>Go to Login</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <Text style={styles.title}>Complete Your Account Setup</Text>
      <Text style={styles.subtitle}>
        Welcome {userInfo?.firstName} {userInfo?.lastName}! Please create a password to complete your account setup.
      </Text>

      <Text style={styles.label}>Create Password</Text>
      <TextInput
        style={styles.input}
        value={password}
        onChangeText={setPassword}
        secureTextEntry
        placeholder="Enter your password"
        autoComplete="new-password"
      />

      <Text style={styles.label}>Confirm Password</Text>
      <TextInput
        style={styles.input}
        value={confirmPassword}
        onChangeText={setConfirmPassword}
        secureTextEntry
        placeholder="Confirm your password"
        autoComplete="new-password"
      />

      <TouchableOpacity
        style={[styles.button, loading && styles.buttonDisabled]}
        onPress={handleSubmit}
        disabled={loading}
      >
        <Text style={styles.buttonText}>
          {loading ? 'Creating Account...' : 'Complete Setup'}
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.linkButton}
        onPress={() => navigate('/login')}
      >
        <Text style={styles.linkText}>Back to Login</Text>
      </TouchableOpacity>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#f8f9fa',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    marginBottom: 30,
    textAlign: 'center',
    lineHeight: 22,
  },
  message: {
    fontSize: 16,
    color: '#666',
    marginBottom: 30,
    textAlign: 'center',
    lineHeight: 22,
  },
  label: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
    alignSelf: 'flex-start',
    width: '100%',
  },
  input: {
    width: '100%',
    padding: 12,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    fontSize: 16,
    marginBottom: 20,
    backgroundColor: '#fff',
  },
  button: {
    width: '100%',
    backgroundColor: '#007AFF',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 15,
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  linkButton: {
    padding: 10,
  },
  linkText: {
    color: '#007AFF',
    fontSize: 16,
  },
});